import React, { useState } from 'react'
import { Button } from '../../../../../../components/ui/button'
import { Loader2, Save } from 'lucide-react'
import { Tab, TabItem } from '../../../../../../components/ui/tab'
import { Input } from '../../../../../../components/ui/input'
import { Label } from '../../../../../../components/ui/label'
import { Switch } from '../../../../../../components/ui/switch'
import NewCheckbox from '../../../../../../components/ui/new-checkbox'
import lodash from 'lodash'
import { useQuery } from '@tanstack/react-query'
import toast, { Toaster } from 'react-hot-toast'
import axios from '../../../../../../lib/axios'
import useAuth from '../../../../../../hooks/useAuth'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../../../redux'
import { GenericCoin } from '../../../../../../components/elements/GenericCoin'
import { GenericNote } from '../../../../../../components/elements/GenericNote'

type Props = {
    denom: KassaDenom
    refetch: () => void
    cn?: string
    orderOnly?: boolean
}

const Denom: React.FC<Props> = ({ denom, refetch, cn, orderOnly = false }) => {
    const { auth } = useAuth()
    const { t } = useTranslation()

    const isNotes = (cn ?? '').toUpperCase() === 'N'
    const [new_denom, setnew_denom] = useState<KassaDenom>(denom)
    const coupureConfig = useSelector((state: RootState) => state.coupure)

    const visibleCoins = new_denom.coins
        .map((value, index) => ({ value, index }))
        .filter(({ value }) =>
            coupureConfig.some((cfg) => cfg.order === true && cfg.cn === 'C' && cfg.value === value.coupure)
        )

    const visibleNotes = new_denom.notes
        .map((value, index) => ({ value, index }))
        .filter(({ value }) =>
            coupureConfig.some((cfg) => cfg.order === true && cfg.cn === 'N' && cfg.value === value.coupure)
        )
        .sort((a, b) => b.value.coupure - a.value.coupure)

    const updateDenomProperty = (type: 'coins' | 'notes', index: number, key: keyof KDenom, value: number) => {
        setnew_denom((prev) => {
            const updated = { ...prev }
            updated[type] = [...prev[type]]
            updated[type][index] = { ...prev[type][index], [key]: value }
            return updated
        })
    }

    const updateFlag = (key: keyof Pick<KassaDenom, 'ordercoins' | 'ordernotes' | 'announcecoins' | 'announcenotes'>, value: boolean) => {
        setnew_denom((prev) => ({ ...prev, [key]: value }))
    }

    const { isFetching, refetch: saveUpdates } = useQuery({
        queryKey: [`update_kassa_denom_${denom.kassaid}`],
        queryFn: async () => {
            try {
                toast.loading(t('settings.toast_loading'), { id: 'saveKassaDenom' })
                const obj = {
                    token:         auth.data?.token,
                    kassaid:       new_denom.kassaid,
                    ordercoins:    new_denom.ordercoins,
                    ordernotes:    new_denom.ordernotes,
                    announcecoins: new_denom.announcecoins,
                    announcenotes: new_denom.announcenotes,
                    coins: new_denom.coins.map((c) => ({ kassaid: c.kassaid, id: c.id, coupure: c.coupure, minqty: c.minqty, maxqty: c.maxqty, perqty: c.perqty })),
                    notes: new_denom.notes.map((n) => ({ kassaid: n.kassaid, id: n.id, coupure: n.coupure, minqty: n.minqty, maxqty: n.maxqty, perqty: n.perqty }))
                }
                const { data } = await axios.post('kassa/denom/createorupdate', obj)
                if (data.errorcode !== 0) { throw new Error(data.message) }
                toast.success(t('settings.toast_success_p2'), { id: 'saveKassaDenom' })
                refetch()
            } catch (error) {
                console.log(error)
                toast.error(t('settings.toast_error_p4'), { id: 'saveKassaDenom' })
            }
            return null
        },
        enabled: false
    })

    if (orderOnly) {
        return (
            <>
                <Toaster />
                <div className='flex justify-between mb-4'>
                    <div>
                        <h2 className='font-semibold leading-none'>{t('settings.title')}</h2>
                        <p className='text-sm text-neutral-400'>{t('settings.order_description')}</p>
                    </div>
                    <Button onClick={() => { saveUpdates() }} disabled={lodash.isEqual(denom, new_denom) || isFetching} className='gap-2'>
                        <Save className='w-5' />
                        {isFetching ? <Loader2 className='animate-spin' /> : t('settings.button_save')}
                    </Button>
                </div>
                <div className='grid grid-cols-2 gap-4 max-w-4xl'>
                    {!isNotes && (
                        <>
                            <div className='flex flex-col gap-1'>
                                <Label htmlFor='order_coins'>{t('settings.order_order_kassa')}</Label>
                                <Switch checked={new_denom.ordercoins} onCheckedChange={(v) => { updateFlag('ordercoins', v) }} id='order_coins' />
                            </div>
                            <div className='flex flex-col gap-1'>
                                <Label htmlFor='announce_coins'>{t('settings.order_announce_kassa')}</Label>
                                <Switch checked={new_denom.announcecoins} onCheckedChange={(v) => { updateFlag('announcecoins', v) }} id='announce_coins' />
                            </div>
                        </>
                    )}
                    {isNotes && (
                        <>
                            <div className='flex flex-col gap-1'>
                                <Label htmlFor='order_notes'>{t('settings.order_notes_kassa')}</Label>
                                <Switch checked={new_denom.ordernotes} onCheckedChange={(v) => { updateFlag('ordernotes', v) }} id='order_notes' />
                            </div>
                            <div className='flex flex-col gap-1'>
                                <Label htmlFor='announce_notes'>{t('settings.announce_notes_kassa')}</Label>
                                <Switch checked={new_denom.announcenotes} onCheckedChange={(v) => { updateFlag('announcenotes', v) }} id='announce_notes' />
                            </div>
                        </>
                    )}
                </div>
            </>
        )
    }

    return (
        <>
            <Toaster />
            <div className='flex justify-between mb-4'>
                <Tab onValueChange={() => {}} value={isNotes ? 'n' : 'c'}>
                    {isNotes
                        ? <TabItem value='n'>{t('settings.notes')}</TabItem>
                        : <TabItem value='c'>{t('settings.coins')}</TabItem>
                    }
                </Tab>
                <Button onClick={() => { saveUpdates() }} disabled={lodash.isEqual(denom, new_denom) || isFetching} className='gap-2'>
                    <Save className='w-5' />
                    {isFetching ? <Loader2 className='animate-spin' /> : t('settings.button_save')}
                </Button>
            </div>

            <div className='grid grid-cols-2 border-b text-xs py-2 mb-2 font-semibold text-neutral-600'>
                <div>{t('settings.denomination')}</div>
                <div className='grid grid-cols-3 gap-4'>
                    <p>{t('settings.minimum')}</p>
                    <p>{t('settings.maximum')}</p>
                    <p>{t('settings.packed')}</p>
                </div>
            </div>

            {!isNotes && (
                <div className='flex flex-col gap-4'>
                    {visibleCoins.map(({ value, index }) => (
                        <div className='grid grid-cols-2' key={index}>
                            <div className='flex items-center gap-4'>
                                <div><NewCheckbox checked={value.maxqty > 0 && value.perqty > 0} disabled /></div>
                                <div className='flex items-center gap-2'>
                                    <div className='w-10 h-10 flex justify-center items-center bg-neutral-50 rounded-sm'>
                                        <GenericCoin value={value.coupure} className='w-8 h-8' />
                                    </div>
                                    <p>EUR {value.coupure}</p>
                                </div>
                            </div>
                            <div className='grid grid-cols-3 gap-4'>
                                <Input value={value.minqty} onChange={({ currentTarget }) => { if (parseFloat(currentTarget.value) > -1) { updateDenomProperty('coins', index, 'minqty', parseFloat(currentTarget.value)) } }} type='number' />
                                <Input value={value.maxqty} onChange={({ currentTarget }) => { if (parseFloat(currentTarget.value) > -1) { updateDenomProperty('coins', index, 'maxqty', parseFloat(currentTarget.value)) } }} type='number' />
                                <Input value={value.perqty} onChange={({ currentTarget }) => { if (parseFloat(currentTarget.value) > -1) { updateDenomProperty('coins', index, 'perqty', parseFloat(currentTarget.value)) } }} type='number' />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {isNotes && (
                <div className='flex flex-col gap-4'>
                    {visibleNotes.map(({ value, index }) => (
                        <div className='grid grid-cols-2' key={index}>
                            <div className='flex items-center gap-4'>
                                <div><NewCheckbox checked={value.maxqty > 0 && value.perqty > 0} disabled /></div>
                                <div className='flex items-center gap-2'>
                                    <div className='flex justify-center items-center w-14 h-7 bg-neutral-50 border rounded-sm'>
                                        <GenericNote value={value.coupure} currency='EUR' className='w-full h-full' />
                                    </div>
                                    <p>EUR {value.coupure}</p>
                                </div>
                            </div>
                            <div className='grid grid-cols-3 gap-4'>
                                <Input value={value.minqty} onChange={({ currentTarget }) => { if (parseFloat(currentTarget.value) > -1) { updateDenomProperty('notes', index, 'minqty', parseFloat(currentTarget.value)) } }} type='number' />
                                <Input value={value.maxqty} onChange={({ currentTarget }) => { if (parseFloat(currentTarget.value) > -1) { updateDenomProperty('notes', index, 'maxqty', parseFloat(currentTarget.value)) } }} type='number' />
                                <Input value={value.perqty} onChange={({ currentTarget }) => { if (parseFloat(currentTarget.value) > -1) { updateDenomProperty('notes', index, 'perqty', parseFloat(currentTarget.value)) } }} type='number' />
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </>
    )
}

export default Denom
